import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminService, settlementService, notificationService } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertTriangle, Download, DollarSign, Building2, FileText, Truck, Archive,
  User, Users, ClipboardList, Settings as SettingsIcon, Package, MapPin,
  BarChart3, Handshake, Wrench, CreditCard, Bell, CheckCircle2, XCircle,
  Inbox, Search, Calendar,
} from 'lucide-react';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [driversAtRisk, setDriversAtRisk] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [notificationStats, setNotificationStats] = useState(null);
  // All Users counts for dashboard tiles
  const [allUsersCounts, setAllUsersCounts] = useState({ total: 0, system_total: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    // Clear stale role cache on every fetch - prevent role coupling issues
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      setError('Authentication required. Please log in again.');
      setLoading(false);
      return;
    }

    setError(null);
    try {
      // Fetch all admin data in parallel with individual error handling
      // Both super_admin and ops_admin use the SAME fetch paths
      const results = await Promise.allSettled([
        adminService.getDashboard(),
        adminService.getAllApplications(),
        adminService.getAllUsers(),
        adminService.getDriversAtRisk(30)
      ]);

      // Process dashboard stats
      if (results[0].status === 'fulfilled') {
        setStats(results[0].value);
      } else {
        console.error('Dashboard stats fetch failed:', results[0].reason);
        // Set default stats to prevent UI breakage
        setStats({ 
          total_users: 0, 
          pending_applications: 0, 
          pending_documents: 0,
          expired_compliance_items: 0,
          user_categories: {}
        });
      }

      // Process applications
      if (results[1].status === 'fulfilled') {
        setApplications(results[1].value || []);
      } else {
        console.error('Applications fetch failed:', results[1].reason);
        setApplications([]);
      }

      // Process users
      if (results[2].status === 'fulfilled') {
        setUsers(results[2].value || []);
      } else {
        console.error('Users fetch failed:', results[2].reason);
        setUsers([]);
      }

      // Process at-risk drivers (non-critical)
      if (results[3].status === 'fulfilled') {
        setDriversAtRisk(results[3].value?.drivers || []);
      } else {
        console.warn('At-risk drivers fetch failed (non-critical):', results[3].reason);
        setDriversAtRisk([]);
      }

      // Try to get notification stats (non-critical, don't block on failure)
      try {
        const notifData = await notificationService.getDeliveryHistory(null, 10);
        setNotificationStats(notifData.stats);
      } catch (e) {
        console.log('Notification stats not available (non-critical)');
      }
      
      // Fetch all-users counts for dashboard tiles (non-critical)
      try {
        const token = localStorage.getItem('token');
        const allUsersResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/all-users?page_size=10`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (allUsersResp.ok) {
          const allUsersData = await allUsersResp.json();
          setAllUsersCounts(allUsersData.counts || { total: 0, system_total: 0 });
          
          // Debug log for super_admin
          if (user?.role === 'super_admin') {
            console.log('[DASHBOARD DEBUG] All Users counts:', allUsersData.counts);
            console.log('[DASHBOARD DEBUG] role_stats total:', results[0].value?.total_users);
          }
        }
      } catch (e) {
        console.log('All users counts not available (non-critical)');
      }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      setError(`Failed to load dashboard data: ${error.message || 'Unknown error'}. Please try refreshing.`);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewApplication = async (applicationId, action, notes) => {
    try {
      await adminService.reviewApplication(applicationId, action, notes);
      alert('Application reviewed successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to review application:', error);
      alert('Failed to review application');
    }
  };

  const handleActivateDriver = async (userId) => {
    try {
      await adminService.activateDriver(userId);
      alert('Driver activated successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to activate driver:', error);
      alert(error.response?.data?.detail || 'Failed to activate driver');
    }
  };

  const handleSuspendDriver = async (userId) => {
    const reason = prompt('Enter suspension reason:');
    if (!reason) return;

    try {
      await adminService.suspendDriver(userId, reason);
      alert('Driver suspended successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to suspend driver:', error);
      alert(error.response?.data?.detail || 'Failed to suspend driver');
    }
  };

  const handleReactivateDriver = async (userId) => {
    try {
      await adminService.reactivateDriver(userId, 'Reactivated by admin');
      alert('Driver reactivated successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to reactivate driver:', error);
      alert(error.response?.data?.detail || 'Failed to reactivate driver');
    }
  };

  // Bulk Actions
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const filteredUsers = getFilteredDrivers();
      setSelectedDrivers(filteredUsers.map(u => u.id));
    } else {
      setSelectedDrivers([]);
    }
  };

  const handleSelectDriver = (userId) => {
    setSelectedDrivers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleBulkSuspend = async () => {
    if (selectedDrivers.length === 0) return;
    const reason = prompt(`Enter suspension reason for ${selectedDrivers.length} driver(s):`);
    if (!reason) return;

    setBulkActionLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const userId of selectedDrivers) {
      try {
        await adminService.suspendDriver(userId, reason);
        successCount++;
      } catch (error) {
        failCount++;
      }
    }

    setBulkActionLoading(false);
    alert(`Bulk suspend complete: ${successCount} succeeded, ${failCount} failed`);
    setSelectedDrivers([]);
    fetchData();
  };

  const handleBulkReactivate = async () => {
    if (selectedDrivers.length === 0) return;
    if (!window.confirm(`Reactivate ${selectedDrivers.length} driver(s)?`)) return;

    setBulkActionLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const userId of selectedDrivers) {
      try {
        await adminService.reactivateDriver(userId);
        successCount++;
      } catch (error) {
        failCount++;
      }
    }

    setBulkActionLoading(false);
    alert(`Bulk reactivate complete: ${successCount} succeeded, ${failCount} failed`);
    setSelectedDrivers([]);
    fetchData();
  };

  const handleExportSettlements = async () => {
    try {
      const blob = await settlementService.exportCSV();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `settlements_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to export settlements');
    }
  };

  const handleExportFactoring = async () => {
    try {
      const blob = await settlementService.exportFactoring();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factoring_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to export factoring data');
    }
  };

  const getFilteredDrivers = useCallback(() => {
    let filtered = users.filter(u => ['driver_applicant', 'active_driver'].includes(u.role));
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(u => u.workflow_state === filterStatus);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(u => 
        u.email?.toLowerCase().includes(query) ||
        u.first_name?.toLowerCase().includes(query) ||
        u.last_name?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [users, filterStatus, searchQuery]);

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-red-100 text-red-800',
      expired_docs: 'bg-orange-100 text-orange-800',
      terminated: 'bg-gray-100 text-gray-800',
      compliance_under_review: 'bg-yellow-100 text-yellow-800',
      compliance_incomplete: 'bg-orange-100 text-orange-800',
      conditional_approval: 'bg-indigo-100 text-indigo-800',
      application_submitted: 'bg-blue-100 text-blue-800',
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            <p className="text-gray-500">Loading admin dashboard...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Error state with retry option
  if (error) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <div className="flex flex-col items-center gap-4 max-w-md text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <p className="text-red-600 font-medium">Dashboard Load Error</p>
            <p className="text-gray-500 text-sm">{error}</p>
            <p className="text-gray-400 text-xs">Role: {user?.role || 'Unknown'}</p>
            <button
              onClick={() => { setLoading(true); setError(null); fetchData(); }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              data-testid="retry-btn"
            >
              Retry
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Admin Control Panel</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Administration, people &amp; compliance control surface — applicants, staff, users, settlements and system settings.</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExportSettlements}
              className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors inline-flex items-center gap-2"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button
              onClick={handleExportFactoring}
              className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors inline-flex items-center gap-2"
            >
              <DollarSign className="w-4 h-4" /> Factoring Export
            </button>
          </div>
        </div>

        {/* Tabs - Mobile Scrollable */}
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex -mb-px min-w-max">
            {['overview', 'applications', 'drivers', 'at-risk', 'settlements'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 px-4 border-b-2 font-medium text-sm whitespace-nowrap capitalize ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                data-testid={`tab-${tab}`}
              >
                {tab.replace('-', ' ')}
                {tab === 'applications' && stats?.pending_applications > 0 && (
                  <span className="ml-2 bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded-full">
                    {stats.pending_applications}
                  </span>
                )}
                {tab === 'at-risk' && driversAtRisk.length > 0 && (
                  <span className="ml-2 bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full">
                    {driversAtRisk.length}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-4 sm:space-y-6">
            {/* User Categories Grid - Clickable KPI Tiles */}
            {stats.user_categories && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {/* Internal Staff - Routes to Staff Directory (SINGLE SOURCE OF TRUTH) */}
                <Link
                  to="/admin/staff-directory"
                  className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border-l-4 border-purple-500 hover:shadow-md hover:bg-purple-50 transition-all cursor-pointer group"
                  data-testid="kpi-internal-staff"
                  aria-label="View Internal Staff Directory"
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="w-6 h-6 text-purple-600 group-hover:scale-110 transition-transform" />
                    <div>
                      <p className="text-xs sm:text-sm text-gray-500">{stats.user_categories.internal_staff?.label || 'Internal Staff'}</p>
                      <p className="text-xl sm:text-2xl font-bold text-purple-600" data-testid="internal-staff-count">
                        {stats.user_categories.internal_staff?.count || 0}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view →</p>
                </Link>

                {/* Driver Applicants - Links to /applicants */}
                <Link
                  to="/applicants?category=applicants"
                  className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border-l-4 border-blue-500 hover:shadow-md hover:bg-blue-50 transition-all cursor-pointer group"
                  data-testid="kpi-driver-applicants"
                  aria-label="View Driver Applicants"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform" />
                    <div>
                      <p className="text-xs sm:text-sm text-gray-500">{stats.user_categories.driver_applicants?.label || 'Driver Applicants'}</p>
                      <p className="text-xl sm:text-2xl font-bold text-blue-600" data-testid="driver-applicants-count">
                        {stats.user_categories.driver_applicants?.count || 0}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view →</p>
                </Link>

                {/* Active Drivers - Links to /onboarding with filter */}
                <Link
                  to="/onboarding?status=active_dispatch_eligible"
                  className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border-l-4 border-green-500 hover:shadow-md hover:bg-green-50 transition-all cursor-pointer group"
                  data-testid="kpi-active-drivers"
                  aria-label="View Active Drivers"
                >
                  <div className="flex items-center gap-3">
                    <Truck className="w-6 h-6 text-green-600 group-hover:scale-110 transition-transform" />
                    <div>
                      <p className="text-xs sm:text-sm text-gray-500">{stats.user_categories.drivers_active?.label || 'Drivers Active'}</p>
                      <p className="text-xl sm:text-2xl font-bold text-green-600" data-testid="drivers-active-count">
                        {stats.user_categories.drivers_active?.count || 0}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view →</p>
                </Link>

                {/* Archived - Links to filtered applicants view */}
                <Link
                  to="/applicants?status=rejected"
                  className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border-l-4 border-gray-400 hover:shadow-md hover:bg-gray-100 transition-all cursor-pointer group"
                  data-testid="kpi-archived"
                  aria-label="View Archived Records"
                >
                  <div className="flex items-center gap-3">
                    <Archive className="w-6 h-6 text-gray-500 group-hover:scale-110 transition-transform" />
                    <div>
                      <p className="text-xs sm:text-sm text-gray-500">{stats.user_categories.archived_inactive?.label || 'Archived'}</p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-600" data-testid="archived-count">
                        {stats.user_categories.archived_inactive?.count || 0}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view →</p>
                </Link>
              </div>
            )}

            {/* Operations Stats Grid - Clickable KPI Tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* System Users - Links to /admin/users?scope=system (Staff + Drivers with accounts) */}
              <Link
                to="/admin/users?scope=system"
                className="bg-white p-4 sm:p-6 rounded-lg shadow-sm hover:shadow-md hover:bg-blue-50 transition-all cursor-pointer group"
                data-testid="kpi-system-users"
                aria-label="View System Users"
              >
                <div className="flex items-center gap-3">
                  <User className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">System Users</p>
                    <p className="text-xl sm:text-2xl font-bold text-blue-600">{allUsersCounts.system_total || stats?.total_users || 0}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1">Staff + Applicants with accounts</p>
                <p className="text-xs text-gray-400 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">Click to view →</p>
              </Link>

              {/* All Records - Links to /admin/users (All users including public applications) */}
              <Link
                to="/admin/users"
                className="bg-white p-4 sm:p-6 rounded-lg shadow-sm hover:shadow-md hover:bg-indigo-50 transition-all cursor-pointer group"
                data-testid="kpi-all-records"
                aria-label="View All Records"
              >
                <div className="flex items-center gap-3">
                  <Users className="w-6 h-6 text-indigo-600 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">All Records</p>
                    <p className="text-xl sm:text-2xl font-bold text-indigo-600">{allUsersCounts.total || 0}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1">Includes public applications</p>
                <p className="text-xs text-gray-400 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">Click to view →</p>
              </Link>

              {/* Pending Apps - Links to /applicants with pending filter */}
              <Link
                to="/applicants?status=new_application"
                className="bg-white p-4 sm:p-6 rounded-lg shadow-sm hover:shadow-md hover:bg-yellow-50 transition-all cursor-pointer group"
                data-testid="kpi-pending-apps"
                aria-label="View Pending Applications"
              >
                <div className="flex items-center gap-3">
                  <ClipboardList className="w-6 h-6 text-yellow-600 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Pending Apps</p>
                    <p className="text-xl sm:text-2xl font-bold text-yellow-600">{stats.pending_applications}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view →</p>
              </Link>

              {/* Pending Docs - Links to /onboarding with docs_submitted filter */}
              <Link
                to="/onboarding?status=docs_submitted"
                className="bg-white p-4 sm:p-6 rounded-lg shadow-sm hover:shadow-md hover:bg-orange-50 transition-all cursor-pointer group"
                data-testid="kpi-pending-docs"
                aria-label="View Pending Documents"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-orange-600 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Pending Docs</p>
                    <p className="text-xl sm:text-2xl font-bold text-orange-600">{stats.pending_documents}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view →</p>
              </Link>
            </div>
            
            {/* Second row of tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Expired Items - Disabled with tooltip */}
              <div 
                className="bg-white p-4 sm:p-6 rounded-lg shadow-sm opacity-60 cursor-not-allowed relative group"
                data-testid="kpi-expired-items"
                title="Expired Items view coming soon"
              >
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Expired Items</p>
                    <p className="text-xl sm:text-2xl font-bold text-red-600">{stats.expired_compliance_items}</p>
                  </div>
                </div>
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                  Coming soon
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <button
                  onClick={() => setActiveTab('applications')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors"
                >
                  <ClipboardList className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                  <span className="text-sm text-gray-700">Review Apps</span>
                </button>
                <button
                  onClick={() => setActiveTab('drivers')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors"
                >
                  <Truck className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                  <span className="text-sm text-gray-700">Manage Drivers</span>
                </button>
                <button
                  onClick={() => setActiveTab('at-risk')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors"
                >
                  <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-amber-600" />
                  <span className="text-sm text-gray-700">At-Risk Drivers</span>
                </button>
                <button
                  onClick={() => setActiveTab('settlements')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors"
                >
                  <DollarSign className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                  <span className="text-sm text-gray-700">Settlements</span>
                </button>
              </div>
            </div>

            {/* System Settings - Phase 4 */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">System Settings</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Link
                  to="/admin/feature-toggles"
                  className="p-4 border border-blue-200 bg-blue-50 rounded-lg hover:bg-blue-100 text-center transition-colors"
                >
                  <SettingsIcon className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                  <span className="text-sm text-blue-700 font-medium">Feature Toggles</span>
                </Link>
                <Link
                  to="/admin/dispatch"
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors"
                >
                  <Package className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                  <span className="text-sm text-gray-700">Dispatch</span>
                </Link>
                <Link
                  to="/admin/loads"
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors"
                >
                  <Truck className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                  <span className="text-sm text-gray-700">Load Management</span>
                </Link>
                <Link
                  to="/admin/tracking"
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors"
                >
                  <MapPin className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                  <span className="text-sm text-gray-700">GPS Tracking</span>
                </Link>
                <Link
                  to="/admin/analytics"
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors"
                >
                  <BarChart3 className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                  <span className="text-sm text-gray-700">Analytics</span>
                </Link>
                <Link
                  to="/admin/broker"
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors"
                >
                  <Handshake className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                  <span className="text-sm text-gray-700">Broker Portal</span>
                </Link>
              </div>
            </div>

            {/* Phase 5 - Staff & Operations */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Staff & Operations</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Link
                  to="/admin/staff"
                  className="p-4 border border-purple-200 bg-purple-50 rounded-lg hover:bg-purple-100 text-center transition-colors"
                >
                  <Users className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                  <span className="text-sm text-purple-700 font-medium">Staff Roles</span>
                </Link>
                <Link
                  to="/admin/maintenance"
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors"
                >
                  <Wrench className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                  <span className="text-sm text-gray-700">Maintenance</span>
                </Link>
                <Link
                  to="/admin/accounting"
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center transition-colors"
                >
                  <CreditCard className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                  <span className="text-sm text-gray-700">Accounting</span>
                </Link>
              </div>
            </div>

            {/* At-Risk Alert */}
            {driversAtRisk.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Bell className="w-6 h-6 text-orange-600" />
                  <div>
                    <h4 className="font-semibold text-orange-900">Attention Required</h4>
                    <p className="text-sm text-orange-800 mt-1">
                      {driversAtRisk.length} driver(s) have documents expiring within 30 days.
                    </p>
                    <button
                      onClick={() => setActiveTab('at-risk')}
                      className="text-sm text-orange-700 hover:text-orange-900 font-medium mt-2"
                    >
                      View Details →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applicant</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Authority</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Submitted</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {applications.filter(app => app.workflow_state === 'application_submitted').map((app) => (
                    <tr key={app.id} className="hover:bg-gray-50" data-testid={`application-row-${app.id}`}>
                      <td className="px-4 sm:px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{app.user?.email}</div>
                          <div className="text-xs sm:text-sm text-gray-500">
                            {app.user?.first_name} {app.user?.last_name}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 hidden sm:table-cell">
                        <span className="text-sm text-gray-500 capitalize">
                          {app.authority_path?.replace(/_/g, ' ') || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                          Pending
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4 hidden sm:table-cell text-sm text-gray-500">
                        {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleReviewApplication(app.id, 'approve', '')}
                            className="px-3 py-1 text-xs sm:text-sm font-medium text-green-700 bg-green-50 rounded hover:bg-green-100 transition-colors inline-flex items-center gap-1"
                            data-testid={`approve-btn-${app.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4" /> Approve
                          </button>
                          <button
                            onClick={() => {
                              const notes = prompt('Enter rejection reason:');
                              if (notes) handleReviewApplication(app.id, 'reject', notes);
                            }}
                            className="px-3 py-1 text-xs sm:text-sm font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 transition-colors inline-flex items-center gap-1"
                            data-testid={`reject-btn-${app.id}`}
                          >
                            <XCircle className="w-4 h-4" /> Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {applications.filter(app => app.workflow_state === 'application_submitted').length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                        <Inbox className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        No pending applications
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Drivers Tab with Bulk Actions */}
        {activeTab === 'drivers' && (
          <div className="space-y-4">
            {/* Filters and Search */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="expired_docs">Expired Docs</option>
                  <option value="compliance_under_review">Under Review</option>
                  <option value="compliance_incomplete">Incomplete</option>
                </select>
              </div>
            </div>

            {/* Bulk Actions Bar */}
            {selectedDrivers.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                <span className="text-sm text-blue-800 font-medium">
                  {selectedDrivers.length} driver(s) selected
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={handleBulkSuspend}
                    disabled={bulkActionLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    {bulkActionLoading ? 'Processing...' : 'Bulk Suspend'}
                  </button>
                  <button
                    onClick={handleBulkReactivate}
                    disabled={bulkActionLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {bulkActionLoading ? 'Processing...' : 'Bulk Reactivate'}
                  </button>
                  <button
                    onClick={() => setSelectedDrivers([])}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Clear Selection
                  </button>
                </div>
              </div>
            )}

            {/* Drivers Table */}
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedDrivers.length === getFilteredDrivers().length && getFilteredDrivers().length > 0}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Role</th>
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getFilteredDrivers().map((user) => (
                      <tr key={user.id} className={`hover:bg-gray-50 ${selectedDrivers.includes(user.id) ? 'bg-blue-50' : ''}`} data-testid={`driver-row-${user.id}`}>
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={selectedDrivers.includes(user.id)}
                            onChange={() => handleSelectDriver(user.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{user.email}</div>
                            <div className="text-xs sm:text-sm text-gray-500">
                              {user.first_name} {user.last_name}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 hidden sm:table-cell text-sm text-gray-500 capitalize">
                          {user.role?.replace(/_/g, ' ')}
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(user.workflow_state)}`}>
                            {user.workflow_state?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <div className="flex flex-wrap gap-2">
                            {user.workflow_state === 'compliance_under_review' && (
                              <button
                                onClick={() => handleActivateDriver(user.id)}
                                className="px-2 py-1 text-xs font-medium text-green-700 bg-green-50 rounded hover:bg-green-100 transition-colors"
                                data-testid={`activate-btn-${user.id}`}
                              >
                                Activate
                              </button>
                            )}
                            {user.workflow_state === 'active' && (
                              <button
                                onClick={() => handleSuspendDriver(user.id)}
                                className="px-2 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 transition-colors"
                                data-testid={`suspend-btn-${user.id}`}
                              >
                                Suspend
                              </button>
                            )}
                            {(user.workflow_state === 'suspended' || user.workflow_state === 'expired_docs') && (
                              <button
                                onClick={() => handleReactivateDriver(user.id)}
                                className="px-2 py-1 text-xs font-medium text-green-700 bg-green-50 rounded hover:bg-green-100 transition-colors"
                                data-testid={`reactivate-btn-${user.id}`}
                              >
                                Reactivate
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {getFilteredDrivers().length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                          <Search className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                          No drivers found matching your criteria
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* At-Risk Drivers Tab */}
        {activeTab === 'at-risk' && (
          <div className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm text-orange-800 inline-flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" /><strong>At-Risk Drivers:</strong> These drivers have compliance documents expiring within 30 days.
              </p>
            </div>

            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expiring Documents</th>
                      <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days Remaining</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {driversAtRisk.map((driver, idx) => (
                      <tr key={driver.user_id || idx} className="hover:bg-gray-50">
                        <td className="px-4 sm:px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{driver.email || 'Unknown'}</div>
                          <div className="text-xs text-gray-500">{driver.first_name} {driver.last_name}</div>
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <div className="space-y-1">
                            {(driver.expiring_documents || []).map((doc, i) => (
                              <span key={i} className="inline-block px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded mr-1">
                                {doc.document_type?.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            driver.days_until_expiry <= 7 ? 'bg-red-100 text-red-800' :
                            driver.days_until_expiry <= 14 ? 'bg-orange-100 text-orange-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {driver.days_until_expiry || '?'} days
                          </span>
                        </td>
                      </tr>
                    ))}
                    {driversAtRisk.length === 0 && (
                      <tr>
                        <td colSpan="3" className="px-6 py-8 text-center text-gray-500">
                          <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
                          No drivers at risk
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Settlements Tab */}
        {activeTab === 'settlements' && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Settlement Management</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <button
                  onClick={handleExportSettlements}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left transition-colors"
                >
                  <BarChart3 className="w-6 h-6 mb-2 text-gray-600" />
                  <span className="font-medium text-gray-900 block">Export All</span>
                  <span className="text-sm text-gray-500">Download CSV of all settlements</span>
                </button>
                <button
                  onClick={handleExportFactoring}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left transition-colors"
                >
                  <DollarSign className="w-6 h-6 mb-2 text-gray-600" />
                  <span className="font-medium text-gray-900 block">Factoring Export</span>
                  <span className="text-sm text-gray-500">Export for invoice factoring</span>
                </button>
                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <Calendar className="w-6 h-6 mb-2 text-gray-600" />
                  <span className="font-medium text-gray-900 block">Weekly Schedule</span>
                  <span className="text-sm text-gray-500">Default pay frequency</span>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50" data-testid="settlement-finalization-card">
                  <CheckCircle2 className="w-6 h-6 mb-2 text-gray-600" />
                  <span className="font-medium text-gray-900 block">Settlement Finalization</span>
                  <span className="text-sm text-gray-500">Manual approval required · Auto-finalize disabled (Controlled Hold)</span>
                </div>
              </div>
            </div>

            {notificationStats && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Delivery Stats</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-900">{notificationStats.total || 0}</p>
                    <p className="text-sm text-gray-500">Total</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{notificationStats.sent || 0}</p>
                    <p className="text-sm text-gray-500">Sent</p>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">{notificationStats.failed || 0}</p>
                    <p className="text-sm text-gray-500">Failed</p>
                  </div>
                  <div className="text-center p-4 bg-yellow-50 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">{notificationStats.pending || 0}</p>
                    <p className="text-sm text-gray-500">Pending</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
